/**
 * Match Stats View - Player statistics display with wagon wheel
 */
import { useState, useEffect } from 'react';
import { axiosInstance } from '../../config/axios';

export default function MatchStatsView({ eventId, fixtureId, stats }) {
  const [activeTab, setActiveTab] = useState('batting');
  const [wagonWheelData, setWagonWheelData] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  const battingStats = stats.filter(s => s.batting_balls > 0)
    .sort((a, b) => b.batting_runs - a.batting_runs);
  
  const bowlingStats = stats.filter(s => s.bowling_overs > 0)
    .sort((a, b) => b.bowling_wickets - a.bowling_wickets || a.bowling_economy - b.bowling_economy);

  const fetchWagonWheel = async (playerId) => {
    try {
      const res = await axiosInstance.get(
        `/v1/events/${eventId}/live/${fixtureId}/wagon-wheel/${playerId}`
      );
      setWagonWheelData(res.data);
      setSelectedPlayer(playerId);
    } catch (err) {
      console.error('Failed to fetch wagon wheel:', err);
    }
  };

  const STAT_TABS = [
    { key: 'batting', label: 'Batting', icon: 'ph-baseball' },
    { key: 'bowling', label: 'Bowling', icon: 'ph-target' },
  ];

  // Wagon wheel region positions (SVG coordinates)
  const WAGON_REGIONS = [
    { id: 1, name: 'Fine Leg', x: 85, y: 165 },
    { id: 2, name: 'Square Leg', x: 35, y: 115 },
    { id: 3, name: 'Mid Wicket', x: 35, y: 65 },
    { id: 4, name: 'Mid On', x: 85, y: 15 },
    { id: 5, name: 'Mid Off', x: 135, y: 15 },
    { id: 6, name: 'Cover', x: 185, y: 65 },
    { id: 7, name: 'Point', x: 185, y: 115 },
    { id: 8, name: 'Third Man', x: 135, y: 165 },
  ];

  if (stats.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <i className="ph-bold ph-chart-bar text-2xl text-gray-400"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-700 mb-2">No Statistics Yet</h3>
        <p className="text-sm text-gray-500">
          Player statistics will appear here once the match begins.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="match-stats-view">
      {/* Tabs */}
      <div className="flex gap-2">
        {STAT_TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
              activeTab === tab.key
                ? 'bg-[#2B3A67] text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            <i className={`ph-bold ${tab.icon}`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Batting Stats */}
      {activeTab === 'batting' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Batter</th>
                <th className="px-3 py-2 text-center">R</th>
                <th className="px-3 py-2 text-center">B</th>
                <th className="px-3 py-2 text-center">4s</th>
                <th className="px-3 py-2 text-center">6s</th>
                <th className="px-3 py-2 text-center">SR</th>
                <th className="px-3 py-2 text-left">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {battingStats.map(player => (
                <tr 
                  key={player.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => fetchWagonWheel(player.player_id)}
                >
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-800">{player.player_name}</span>
                      {player.is_player_of_match && (
                        <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] rounded font-bold">MoM</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">{player.team_name}</span>
                  </td>
                  <td className="px-3 py-3 text-center font-bold text-[#2B3A67]">{player.batting_runs}</td>
                  <td className="px-3 py-3 text-center text-gray-600">{player.batting_balls}</td>
                  <td className="px-3 py-3 text-center text-blue-600">{player.batting_fours}</td>
                  <td className="px-3 py-3 text-center text-purple-600">{player.batting_sixes}</td>
                  <td className="px-3 py-3 text-center text-gray-600">{player.batting_strike_rate.toFixed(1)}</td>
                  <td className="px-3 py-3 text-left">
                    <span className={`text-xs ${player.how_out ? 'text-red-600' : 'text-green-600'}`}>
                      {player.how_out || 'not out'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Bowling Stats */}
      {activeTab === 'bowling' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Bowler</th>
                <th className="px-3 py-2 text-center">O</th>
                <th className="px-3 py-2 text-center">M</th>
                <th className="px-3 py-2 text-center">R</th>
                <th className="px-3 py-2 text-center">W</th>
                <th className="px-3 py-2 text-center">Econ</th>
                <th className="px-3 py-2 text-center">Dots</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bowlingStats.map(player => (
                <tr key={player.id} className="hover:bg-gray-50">
                  <td className="px-3 py-3">
                    <div className="font-medium text-gray-800">{player.player_name}</div>
                    <span className="text-xs text-gray-400">{player.team_name}</span>
                  </td>
                  <td className="px-3 py-3 text-center text-gray-600">{player.bowling_overs.toFixed(1)}</td>
                  <td className="px-3 py-3 text-center text-gray-600">{player.bowling_maidens}</td>
                  <td className="px-3 py-3 text-center text-gray-600">{player.bowling_runs}</td>
                  <td className="px-3 py-3 text-center font-bold text-[#2B3A67]">{player.bowling_wickets}</td>
                  <td className="px-3 py-3 text-center text-gray-600">{player.bowling_economy.toFixed(2)}</td>
                  <td className="px-3 py-3 text-center text-gray-600">{player.bowling_dots}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Wagon Wheel Modal */}
      {wagonWheelData && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-bold text-[#2B3A67]">
              Wagon Wheel - {stats.find(s => s.player_id === selectedPlayer)?.player_name}
            </h4>
            <button 
              onClick={() => setWagonWheelData(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <i className="ph-bold ph-x"></i>
            </button>
          </div>
          
          {/* Simple Wagon Wheel Visualization */}
          <div className="flex justify-center">
            <svg width="220" height="200" viewBox="0 0 220 200">
              {/* Field circle */}
              <circle cx="110" cy="100" r="80" fill="#e8f5e9" stroke="#4caf50" strokeWidth="2" />
              {/* Pitch */}
              <rect x="100" y="80" width="20" height="40" fill="#c8b560" />
              
              {/* Region segments */}
              {WAGON_REGIONS.map(region => {
                const data = wagonWheelData.regions[region.id] || { runs: 0, balls: 0 };
                const intensity = Math.min(data.runs / 20, 1); // Max intensity at 20 runs
                return (
                  <g key={region.id}>
                    <circle 
                      cx={region.x} 
                      cy={region.y} 
                      r={15 + (intensity * 15)}
                      fill={`rgba(58, 175, 169, ${0.2 + intensity * 0.6})`}
                      stroke="#3AAFA9"
                      strokeWidth="1"
                    />
                    <text 
                      x={region.x} 
                      y={region.y} 
                      textAnchor="middle" 
                      dy="4"
                      fontSize="12"
                      fontWeight="bold"
                      fill="#2B3A67"
                    >
                      {data.runs}
                    </text>
                  </g>
                );
              })}
            </svg>
          </div>
          
          {/* Stats Summary */}
          <div className="mt-4 grid grid-cols-4 gap-2 text-center text-sm">
            <div className="bg-gray-50 rounded p-2">
              <p className="font-bold text-[#2B3A67]">{wagonWheelData.total_shots}</p>
              <p className="text-xs text-gray-500">Shots</p>
            </div>
            <div className="bg-blue-50 rounded p-2">
              <p className="font-bold text-blue-600">
                {Object.values(wagonWheelData.regions).reduce((sum, r) => sum + r.fours, 0)}
              </p>
              <p className="text-xs text-gray-500">Fours</p>
            </div>
            <div className="bg-purple-50 rounded p-2">
              <p className="font-bold text-purple-600">
                {Object.values(wagonWheelData.regions).reduce((sum, r) => sum + r.sixes, 0)}
              </p>
              <p className="text-xs text-gray-500">Sixes</p>
            </div>
            <div className="bg-green-50 rounded p-2">
              <p className="font-bold text-green-600">
                {Object.values(wagonWheelData.regions).reduce((sum, r) => sum + r.runs, 0)}
              </p>
              <p className="text-xs text-gray-500">Total</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
